/**
 * RemindersWidget -- Today's reminders/alarms with add, countdown, and Notification API.
 *
 * Data stored in IndexedDB `aweni_reminders` store.
 * Widget #18 in WidgetEngine, after ReadingProgress.
 */

import type { Widget, WidgetContext } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Reminder {
  id: string;
  title: string;
  time: string; // "HH:MM"
  repeat: 'once' | 'daily' | 'weekly';
  weekday?: number; // 0-6 for weekly
  createdAt: number;
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_NAME = 'aweni_reminders_db';
const STORE_NAME = 'aweni_reminders';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function loadReminders(): Promise<Reminder[]> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

async function saveReminder(reminder: Reminder): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(reminder);
  } catch {
    // Silent fail
  }
}

async function deleteReminder(id: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(id);
  } catch {
    // Silent fail
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTodayReminders(reminders: Reminder[]): Reminder[] {
  const now = new Date();
  const today = now.getDay(); // 0-6

  return reminders
    .filter((r) => {
      if (r.repeat === 'daily') return true;
      if (r.repeat === 'weekly') return r.weekday === today;
      // 'once' -- check if created today
      const created = new Date(r.createdAt);
      return (
        created.getFullYear() === now.getFullYear() &&
        created.getMonth() === now.getMonth() &&
        created.getDate() === now.getDate()
      );
    })
    .sort((a, b) => a.time.localeCompare(b.time));
}

function isReminderPast(timeStr: string): boolean {
  const [h, m] = timeStr.split(':').map(Number);
  const now = new Date();
  return now.getHours() > h || (now.getHours() === h && now.getMinutes() >= m);
}

function getCountdown(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const now = new Date();
  const target = new Date();
  target.setHours(h, m, 0, 0);
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return '';
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  if (hrs > 0) return `${hrs}h${String(remainMins).padStart(2, '0')}m`;
  return `${remainMins}m`;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ---------------------------------------------------------------------------
// Widget
// ---------------------------------------------------------------------------

let cachedReminders: Reminder[] = [];
let loaded = false;

export const RemindersWidget: Widget = {
  id: 'reminders',
  label: {
    en: 'Reminders',
    fr: 'Rappels',
    ar: '\u0627\u0644\u062a\u0630\u0643\u064a\u0631\u0627\u062a',
    es: 'Recordatorios',
    de: 'Erinnerungen',
  },
  icon: '\u{23F0}',
  defaultVisible: true,
  defaultOrder: 18,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    // Load reminders (async, but we render immediately with cached data)
    if (!loaded) {
      loadReminders().then((r) => {
        cachedReminders = r;
        loaded = true;
        // Re-render with loaded data
        renderContent(container, size, ctx);
      });
    }
    renderContent(container, size, ctx);
  },
};

function renderContent(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
  const today = getTodayReminders(cachedReminders);
  const upcoming = today.filter((r) => !isReminderPast(r.time));
  const past = today.filter((r) => isReminderPast(r.time));

  if (size === 'compact') {
    // Show next 2 upcoming reminders
    const next2 = upcoming.slice(0, 2);
    if (next2.length === 0 && past.length === 0) {
      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-action="add">
          <div class="flex items-center gap-2">
            <span>\u{23F0}</span>
            <span class="text-sm" style="color:var(--text-secondary);">${ctx.t('reminder_no_reminders')}</span>
            <button class="text-xs px-3 py-1 rounded-full" style="background:#6366F120;color:#6366F1;">+ ${ctx.t('reminder_add')}</button>
          </div>
        </div>`;
    } else {
      const itemsHtml = next2
        .map(
          (r) =>
            `<div class="flex items-center justify-between">
              <span class="text-sm" style="color:var(--text-primary);">${r.title}</span>
              <div class="flex items-center gap-2">
                <span class="text-xs font-mono" style="color:var(--text-secondary);">${r.time}</span>
                <span class="text-xs font-bold" style="color:var(--accent);">${getCountdown(r.time)}</span>
              </div>
            </div>`
        )
        .join('');

      container.innerHTML = `
        <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span>\u{23F0}</span>
              <span class="text-sm font-semibold" style="color:var(--text-primary);">${ctx.t('reminder_title')}</span>
            </div>
            <button class="reminder-add-btn text-xs px-3 py-1 rounded-full" style="background:#6366F120;color:#6366F1;">+</button>
          </div>
          <div class="space-y-2">${itemsHtml}</div>
        </div>`;
    }
  } else {
    // Expanded: all today's reminders + add form
    const upcomingHtml = upcoming
      .map(
        (r) =>
          `<div class="flex items-center justify-between p-2 rounded-xl" style="background:var(--bg-secondary);">
            <div>
              <p class="text-sm font-medium" style="color:var(--text-primary);">${r.title}</p>
              <p class="text-xs" style="color:var(--text-secondary);">${r.time} ${r.repeat !== 'once' ? '(\u{1F501} ' + ctx.t('reminder_repeat_' + r.repeat) + ')' : ''}</p>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm font-bold font-mono" style="color:var(--accent);">${getCountdown(r.time)}</span>
              <button class="reminder-delete text-xs" style="color:var(--text-secondary);cursor:pointer;" data-id="${r.id}">\u2715</button>
            </div>
          </div>`
      )
      .join('');

    const pastHtml = past
      .map(
        (r) =>
          `<div class="flex items-center justify-between p-2 rounded-xl" style="background:var(--bg-secondary);opacity:0.5;">
            <div>
              <p class="text-sm" style="color:var(--text-secondary);">\u2713 ${r.title}</p>
              <p class="text-xs" style="color:var(--text-secondary);">${r.time}</p>
            </div>
            <button class="reminder-delete text-xs" style="color:var(--text-secondary);cursor:pointer;" data-id="${r.id}">\u2715</button>
          </div>`
      )
      .join('');

    container.innerHTML = `
      <div class="rounded-2xl p-5" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold uppercase tracking-wide" style="color:var(--text-secondary);">\u{23F0} ${ctx.t('reminder_title')}</h3>
          <button class="reminder-toggle-form text-xs px-3 py-1 rounded-full" style="background:#6366F120;color:#6366F1;">+ ${ctx.t('reminder_add')}</button>
        </div>

        <div class="reminder-form hidden mb-3 p-3 rounded-xl" style="background:var(--bg-secondary);">
          <input class="reminder-title-input w-full mb-2 p-2 rounded-lg text-sm" style="background:var(--card-bg);color:var(--text-primary);border:1px solid var(--border);" placeholder="${ctx.t('reminder_title_placeholder')}" />
          <div class="flex items-center gap-2 mb-2">
            <input class="reminder-time-input p-2 rounded-lg text-sm" type="time" style="background:var(--card-bg);color:var(--text-primary);border:1px solid var(--border);" />
            <select class="reminder-repeat-input p-2 rounded-lg text-sm" style="background:var(--card-bg);color:var(--text-primary);border:1px solid var(--border);">
              <option value="once">${ctx.t('reminder_once')}</option>
              <option value="daily">${ctx.t('reminder_daily')}</option>
              <option value="weekly">${ctx.t('reminder_weekly')}</option>
            </select>
          </div>
          <button class="reminder-save-btn w-full py-2 rounded-lg text-sm font-medium" style="background:#6366F1;color:white;">${ctx.t('common_save')}</button>
        </div>

        ${upcoming.length > 0 ? `<div class="space-y-2 mb-2">${upcomingHtml}</div>` : ''}
        ${past.length > 0 ? `<div class="space-y-2 mb-2">${pastHtml}</div>` : ''}
        ${upcoming.length === 0 && past.length === 0 ? `<p class="text-sm text-center py-4" style="color:var(--text-secondary);">${ctx.t('reminder_no_reminders')}</p>` : ''}

        <p class="text-xs text-center mt-3" style="color:var(--text-secondary);">${ctx.t('reminder_yawmi_link')}</p>
      </div>`;

    // Toggle form
    const toggleBtn = container.querySelector('.reminder-toggle-form');
    const form = container.querySelector('.reminder-form');
    if (toggleBtn && form) {
      toggleBtn.addEventListener('click', () => {
        form.classList.toggle('hidden');
      });
    }

    // Save reminder
    const saveBtn = container.querySelector('.reminder-save-btn');
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        const titleInput = container.querySelector('.reminder-title-input') as HTMLInputElement;
        const timeInput = container.querySelector('.reminder-time-input') as HTMLInputElement;
        const repeatInput = container.querySelector('.reminder-repeat-input') as HTMLSelectElement;

        if (!titleInput.value.trim() || !timeInput.value) return;

        const reminder: Reminder = {
          id: generateId(),
          title: titleInput.value.trim(),
          time: timeInput.value,
          repeat: repeatInput.value as 'once' | 'daily' | 'weekly',
          weekday: repeatInput.value === 'weekly' ? new Date().getDay() : undefined,
          createdAt: Date.now(),
        };

        await saveReminder(reminder);
        cachedReminders.push(reminder);
        renderContent(container, size, ctx);

        // Schedule notification
        scheduleNotification(reminder);
        ctx.showNotification(ctx.t('reminder_saved'), 'success');
      });
    }

    // Delete buttons
    container.querySelectorAll('.reminder-delete').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = (btn as HTMLElement).dataset.id;
        if (id) {
          await deleteReminder(id);
          cachedReminders = cachedReminders.filter((r) => r.id !== id);
          renderContent(container, size, ctx);
        }
      });
    });
  }

  // Add button (compact mode)
  const addBtn = container.querySelector('.reminder-add-btn') || container.querySelector('[data-action="add"]');
  if (addBtn) {
    addBtn.addEventListener('click', () => {
      // Switch to expanded view to show form
      const wrapper = container.closest('.widget-wrapper');
      if (wrapper) {
        // Trigger expand via widget engine would be ideal, but for now show a simple prompt
        const title = prompt(ctx.t('reminder_title_placeholder'));
        if (!title) return;
        const time = prompt(ctx.t('reminder_time_placeholder'), new Date().toTimeString().slice(0, 5));
        if (!time) return;

        const reminder: Reminder = {
          id: generateId(),
          title,
          time,
          repeat: 'once',
          createdAt: Date.now(),
        };

        saveReminder(reminder).then(() => {
          cachedReminders.push(reminder);
          renderContent(container, size, ctx);
          scheduleNotification(reminder);
        });
      }
    });
  }
}

function scheduleNotification(reminder: Reminder): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  const [h, m] = reminder.time.split(':').map(Number);
  const now = new Date();
  const target = new Date();
  target.setHours(h, m, 0, 0);

  const delay = target.getTime() - now.getTime();
  if (delay <= 0) return;

  setTimeout(() => {
    try {
      new Notification(reminder.title, {
        body: `${reminder.time} - ${reminder.title}`,
        icon: '/icons/icon-192x192.png',
        tag: `reminder-${reminder.id}`,
      });
    } catch {
      // Silent
    }
  }, delay);
}
